class CookieHelper {
  /**
   * Set a cookie with security options
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} days - Expiration in days (optional, defaults to 7)
   * @param {Object} options - Additional cookie options
   */
  static setCookie(name, value, days = 7, options = {}) {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    
    const defaultOptions = {
      expires: expires.toUTCString(),
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'Strict',
      httpOnly: false // Note: httpOnly can't be set from client-side JavaScript
    };

    const cookieOptions = { ...defaultOptions, ...options };
    
    let cookieString = `${name}=${encodeURIComponent(value)}`;
    
    Object.keys(cookieOptions).forEach(key => {
      if (cookieOptions[key] !== null && cookieOptions[key] !== undefined) {
        cookieString += `; ${key}=${cookieOptions[key]}`;
      }
    });
    
    document.cookie = cookieString;
  }

  /**
   * Get a cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null if not found
   */
  static getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }
    
    return null;
  }

  /**
   * Delete a cookie by name
   * @param {string} name - Cookie name
   * @param {Object} options - Cookie options (path, domain)
   */
  static deleteCookie(name, options = {}) {
    const defaultOptions = {
      path: '/',
      expires: 'Thu, 01 Jan 1970 00:00:00 GMT'
    };

    const cookieOptions = { ...defaultOptions, ...options };
    
    let cookieString = `${name}=;`;
    
    Object.keys(cookieOptions).forEach(key => {
      if (cookieOptions[key] !== null && cookieOptions[key] !== undefined) {
        cookieString += ` ${key}=${cookieOptions[key]}`;
      }
    });
    
    document.cookie = cookieString;
  }

  /**
   * Check if a cookie exists
   * @param {string} name - Cookie name
   * @returns {boolean} True if cookie exists
   */
  static hasCookie(name) {
    return this.getCookie(name) !== null;
  }

  /**
   * Set authentication cookies
   * @param {Object} authData - Authentication data
   */
  static setAuthCookies(authData) {
    if (authData.AccessToken) {
      this.setCookie('AccessToken', authData.AccessToken, 30); // 30 days
    }
    
    if (authData.UserID) {
      this.setCookie('UserID', authData.UserID, 30);
    }
    
    if (authData.UserName) {
      this.setCookie('UserName', authData.UserName, 30);
    }
    
    if (authData.ClientID) {
      this.setCookie('ClientID', authData.ClientID, 30);
    }
  }

  /**
   * Get authentication data from cookies
   * @returns {Object} Authentication data
   */
  static getAuthCookies() {
    return {
      AccessToken: this.getCookie('AccessToken'),
      UserID: this.getCookie('UserID'),
      UserName: this.getCookie('UserName'),
      ClientID: this.getCookie('ClientID')
    };
  }

  /**
   * Clear all authentication cookies
   */
  static clearAuthCookies() {
    this.deleteCookie('AccessToken');
    this.deleteCookie('UserID');
    this.deleteCookie('UserName');
    this.deleteCookie('ClientID');
  }

  /**
   * Get all cookies as an object
   * @returns {Object} All cookies
   */
  static getAllCookies() {
    const cookies = {};
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }
      const eqIndex = c.indexOf('=');
      if (eqIndex > 0) {
        const name = c.substring(0, eqIndex);
        const value = decodeURIComponent(c.substring(eqIndex + 1));
        cookies[name] = value;
      }
    }
    
    return cookies;
  }
}

export default CookieHelper;
